/**
 * React Query hooks for Reports API
 */

import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../services/reports';
import type { ReportQueryParams } from '../types/api';

// =============================================================================
// Query Keys
// =============================================================================

export const reportsKeys = {
  all: ['reports'] as const,
  projectAll: (projectId: string) =>
    [...reportsKeys.all, 'project', projectId] as const,
  project: (projectId: string, params?: ReportQueryParams) =>
    [...reportsKeys.all, 'project', projectId, params ?? {}] as const,
  global: (filters?: { status?: string; days?: number }) =>
    [...reportsKeys.all, 'global', filters] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to fetch project report with server-side filtering & pagination.
 *
 * When `params` includes `page`, the response will contain a `pagination` field
 * and only that page's scenarios with full step data.
 *
 * When filters are provided (domain, area, tester, status, etc.), breakdowns
 * and stats in the response reflect the filtered subset.
 */
export function useProjectReport(
  projectId: string,
  params?: ReportQueryParams,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  return useQuery({
    queryKey: reportsKeys.project(projectId, params),
    queryFn: () => reportsApi.getProjectReport(projectId, params),
    enabled: (options?.enabled ?? true) && !!projectId,
    refetchInterval: options?.refetchInterval,
    staleTime: 30000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch global analytics across all projects
 */
export function useGlobalReport(filters?: {
  status?: 'all' | 'active' | 'inactive';
  days?: number;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: reportsKeys.global(filters),
    queryFn: () =>
      reportsApi.getGlobalReport({
        status: filters?.status,
        days: filters?.days,
      }),
    staleTime: 60000,
    refetchInterval: filters?.refetchInterval,
  });
}
